import React, { useState, useEffect, useRef } from "react";
import {
  handleRemoveRegistration,
  handleEditTeamName,
} from "services/registrationService";
import { canManage } from "services/tournamentService";

const TournamentParticipantsList = ({
  tournament,
  user,
  registrations,
  userDetails,
  locationRankings = [],
}) => {
  const [editingTeamName, setEditingTeamName] = useState(null);
  const [newTeamName, setNewTeamName] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const dropdownRef = useRef(null);
  const isAdmin = canManage(user, tournament.locationId);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getUserRanking = (userId) => {
    const rankingEntry = locationRankings.find((r) => r.userId === userId);
    return rankingEntry ? rankingEntry.ranking : null;
  };

  const submitTeamNameEdit = async (regId) => {
    if (!newTeamName.trim()) return;
    await handleEditTeamName(regId, newTeamName);
    setEditingTeamName(null);
    setNewTeamName("");
  };

  const handleRemoveWithConfirmation = (registrationId) => {
    const confirmed = window.confirm(
      "Are you sure you want to remove this registration?"
    );
    if (confirmed) {
      handleRemoveRegistration(registrationId);
    }
  };

  return (
    <details className="mt-2 w-full bg-gray-600 p-2 rounded-md">
      <summary className="text-lg font-semibold text-white cursor-pointer">
        Voir les participants
        <span className="ml-4 text-violet-400">
          ({(registrations || []).length}) 📝
        </span>
      </summary>

      <ul className="mt-2 text-gray-300">
        {registrations?.map((reg, index) => {
          const userInfo = userDetails[reg.userId];
          const ranking = getUserRanking(reg.userId);
          const isWaitingListStart = index === tournament.waitingListLimit;

          return (
            <li key={reg.id}>
              {isWaitingListStart && (
                <div className="p-1 my-1 text-sm font-semibold text-yellow-400 border-b border-l border-yellow-500 bg-gray-700 rounded-md">
                  Liste d'attente:
                </div>
              )}
              <div
                className="relative flex justify-between items-center px-2 py-2 bg-gray-800 mb-[1px] rounded-sm"
                ref={dropdownRef}
              >
                <div>
                  {editingTeamName === reg.id ? (
                    <>
                      <input
                        type="text"
                        value={newTeamName}
                        onChange={(e) => setNewTeamName(e.target.value)}
                        className="w-full px-3 py-1 text-gray-900 rounded-md focus:outline-none text-sm focus:ring-2 focus:ring-blue-500"
                        onBlur={() => submitTeamNameEdit(reg.id)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && submitTeamNameEdit(reg.id)
                        }
                      />
                      {isAdmin && (
                        <button
                          className="text-white bg-red-600 hover:bg-red-500 mt-2 p-2 rounded-md text-sm font-semibold"
                          onClick={() =>
                            handleRemoveWithConfirmation(reg.id)
                          }
                          title="Supprimer"
                        >
                          Supprimer l'inscription
                        </button>
                      )}
                    </>
                  ) : (
                    <span
                      className={`block truncate whitespace-nowrap overflow-hidden text-xs md:text-sm xl:text-base ${
                        isAdmin
                          ? "max-w-[200px] lg:max-w-[400px]"
                          : "max-w-[250px] lg:max-w-[400px]"
                      }`}
                      title={reg.teamName || "Équipe inconnue"}
                    >
                      {`${index + 1}. ${reg.teamName || "Équipe inconnue"}`}
                    </span>
                  )}
                </div>

                <div className="flex space-x-2">
                  {isAdmin &&
                    (editingTeamName === reg.id ? (
                      <button
                        className="text-green-500 hover:text-green-300 ml-2 text-lg"
                        onClick={() => submitTeamNameEdit(reg.id)}
                        title="Confirmer"
                      >
                        ✅
                      </button>
                    ) : (
                      <button
                        className="text-white hover:text-gray-300 ml-2"
                        onClick={() => {
                          setEditingTeamName(reg.id);
                          setNewTeamName(reg.teamName || reg.name);
                        }}
                        title="Modifier"
                      >
                        ✏️
                      </button>
                    ))}

                  {isAdmin && userInfo && (
                    <button
                      className="text-white text-lg rounded-lg"
                      onMouseDown={(e) => {
                        e.stopPropagation(); // optional: blocks propagation if needed
                        setOpenDropdown((prev) =>
                          prev === reg.id ? null : reg.id
                        );
                      }}
                      title="Info"
                    >
                      ℹ️
                    </button>
                  )}
                </div>
              </div>

              {/* Inline info box below each registration */}
              {openDropdown === reg.id && (
                <div className="my-1 p-4 bg-red-900 text-white text-sm md:text-base rounded-b-md">
                  {reg.teamMateId && (
                    <p className="font-semibold text-white">{reg.teamName}</p>
                  )}
                  <p className="font-semibold text-white">
                    {userInfo.name || "N/A"}
                  </p>
                  <p>
                    <strong>Email:</strong> {userInfo.email || "N/A"}
                  </p>
                  <p>
                    <strong>Téléphone:</strong> {userInfo.phone || "N/A"}
                  </p>
                  {ranking !== null && (
                    <span className="text-sm text-amber-400 ml-2">
                      (Elo: {ranking})
                    </span>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {showContactPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg shadow-xl text-center">
            <p className="text-lg font-semibold text-gray-800 mb-4">
              Les inscriptions sont fermées. Veuillez contacter l'organisateur
              pour retirer votre inscription.
            </p>
            <button
              onClick={() => setShowContactPopup(false)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg transition"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </details>
  );
};

export default TournamentParticipantsList;
